import { PdsDialog } from './pds-dialog';
import { ChatThreadTypeEnum } from 'common-utils';
import { pdsI18n } from './pds-i18n';
import { ChatMessageRI, ChatParticipantRI, ChatThreadRI, ChatOperationRI } from 'common-utils/rest-interfaces';
import { pdsHttpUtils } from './pds-http-utils';

type ParticipantKeyValue = { [userUid: string]: ChatParticipantRI };
export type MessageGroup = { participant: ChatParticipantRI, messages: Array<ChatMessageRI> };

export class PdsChatQuickMessage extends PdsDialog {
  is = 'pds-chat-quick-message';

  chatThread: ChatThreadRI;
  currentUserType: string;
  currentUserUid: string;
  topicUsername: string;
  sectionUids: Array<string>;
  inputValue: string;
  currentTab: string;
  isParentContactInfo: boolean;

  getS3MediaPath: () => string;

  ready(): void {
    super.ready();
    this.getS3MediaPath = this.pds.preferences.getS3MediaPath;
    if (this.currentUserType === 'TEACHER' && this.chatThread.threadType === ChatThreadTypeEnum.TEACHER_PARENT) {
      this.currentTab = 'PARENT_TAB';
    } else {
      this.currentTab = 'STUDENT_TAB';
    }
  }

  attached(): void {
    super.attached();
    setTimeout(() => {
      let containerEl = <HTMLElement>this.querySelector('.pds-chat-message-container');
      containerEl.scrollTop = containerEl.scrollHeight;
    });
  }

  computeClass(msgGroup: MessageGroup): string {
    return this.isCurrentUser(msgGroup.participant.userUid) ? 'pds-quick-message-group pds-chat-message-group-right' : 'pds-quick-message-group';
  }

  getModalHeader(chatThread: ChatThreadRI, userType: string, userName: string): string {
    if (chatThread == null || userType == null) {
      return '';
    }
    switch (userType) {
      case 'TEACHER':
        return this._getModalHeaderForTeacher(chatThread, userName);
      case 'STUDENT':
      case 'PARENT':
        return this._getModalHeaderForStudentOrParent(chatThread);
      default:
        console.error(`ChatThread does not handle userType ${userType} yet.`);
        return '';

    }
  }

  getMessageGroups(messages: Array<ChatMessageRI>): Array<MessageGroup> {
    if (messages == null || messages.length === 0) {
      return [];
    }
    let participantKeyValues: Array<ParticipantKeyValue> = this.chatThread.participants
      .map((participant) => {
        return { [participant.userUid]: participant };
      });
    let participantMap: ParticipantKeyValue = Object.assign({}, ...participantKeyValues); //Build map using ninja technique
    let currentParticipantUid: string = messages[0].senderUid;
    let messageGroups: Array<MessageGroup> = [];
    let currentMessageGroup: MessageGroup = { participant: participantMap[currentParticipantUid], messages: [] };
    messageGroups.push(currentMessageGroup);

    for (let message of messages) {
      if (message.senderUid !== currentParticipantUid) {
        currentParticipantUid = message.senderUid;
        currentMessageGroup = { participant: participantMap[message.senderUid], messages: [message] };
        messageGroups.push(currentMessageGroup);
      } else {
        currentMessageGroup.messages.push(message);
      }
    }
    return messageGroups;
  }

  getLastMessageTime(messageGroup: MessageGroup): string {
    let message = messageGroup.messages[messageGroup.messages.length - 1];
    return pdsI18n.formatDate(new Date(message.sentAt!), 'DATE_TIME_PRETTY'); // the ! is safe because sentAt is only null on POST
  }

  getParticipantName(participant: ChatParticipantRI): string {
    if (participant.userType === 'TEACHER' && participant.title != null) {
      return `${participant.title} ${participant.lastName}`;
    }
    return `${participant.firstName} ${participant.lastName}`;
  }

  getParticipantImagePath(participant: ChatParticipantRI): string {
    return `/image/${participant.userType.toLowerCase()}-${participant.userUid}`;
  }

  handleNavigateClick(event: Event): void {
    event.preventDefault();
    this.fire('pds-quick-chat-navigation', this.chatThread);
    this.closeDialog();
  }

  isCurrentUser(userUid: string): boolean {
    return this.currentUserUid === userUid;
  }

  sendMessage(event: Event): void {
    event.preventDefault();
    let message = {
      operation: 'CREATE',
      threadUid: this.chatThread.uid,
      senderUid: this.currentUserUid,
      associatedSectionUids: this.sectionUids,
      senderUserType: this.currentUserType,
      body: this.inputValue,
      clientKey: `${this.chatThread.uid}:${(new Date()).getTime().toString()}` // we use this as a key for new messages since the requests are handled in a kafka queue asynchronously
    };
    pdsHttpUtils.post(`${this.pds.preferences.host}/communication/v1/queue`, message, {}, pdsHttpUtils.addUCAuthHeader({}))
      .subscribe(() => {
        this.fire('pds-quick-message-sent', true);
        this.closeDialog();
      });
  }

  setCurrentTab(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.currentTab = (<any>event).currentTarget.dataset.threadType;
    if (this.currentTab === 'STUDENT_TAB' || (this.currentTab === 'PARENT_TAB' && this.isParentContactInfo)) {
      this.fire('pds-quick-chat-tab-change', this.currentTab);
    } else {
      this.currentTab = 'STUDENT_TAB';
    }
  }

  getParentHeaderTooltipText(isParentContactInfo: boolean): string {
    let tooltipText = '';
    if (isParentContactInfo) {
      tooltipText = pdsI18n.getText(`pds|quick chat|Switch to message guardians`);
    } else {
      tooltipText = pdsI18n.getText(`pds|quick chat|No Active Account`);
    }
    return tooltipText;
  }

  private _getModalHeaderForTeacher(chatThread: ChatThreadRI, userName: string): string {
    switch (chatThread.threadType) {
      case ChatThreadTypeEnum.TEACHER_PARENT:
        return pdsI18n.getText(`pds|quick chat|Send xx s Parents/Guardians a Message`, { studentName: userName });
      case ChatThreadTypeEnum.STUDENT_TEACHER:
        return pdsI18n.getText(`pds|quick chat|Send xx a Message`, { recipientName: userName });
      default:
        console.error(`ChatThread Type ${chatThread.threadType} not handled yet in pds-chat-quick-message`);
        return pdsI18n.getText(`pds|quick chat|Send a Message`);
    }
  }

  private _getModalHeaderForStudentOrParent(chatThread: ChatThreadRI): string {
    let teacherParticipant = chatThread.participants.find((participant) => {
      return participant.userType === 'TEACHER';
    });
    if (!teacherParticipant) {
      throw 'ChatThread does not have a teacher participant';
    }
    let teacherName: string;
    if (teacherParticipant.title) {
      teacherName = `${teacherParticipant.title} ${teacherParticipant.lastName}`;
    } else {
      teacherName = `${teacherParticipant.firstName} ${teacherParticipant.lastName}`;
    }

    switch (chatThread.threadType) {
      case ChatThreadTypeEnum.TEACHER_PARENT:
      case ChatThreadTypeEnum.STUDENT_TEACHER:
        return pdsI18n.getText(`pds|quick chat|Send xx a Message`, { recipientName: teacherName });
      default:
        console.error(`ChatThread Type ${chatThread.threadType} not handled yet in pds-chat-quick-message`);
        return pdsI18n.getText(`pds|quick chat|Send a Message`);
    }
  }
}
